import * as msal from "@azure/msal-browser";
import Vue from "vue";

let msalInstance;
let msalPluginInstance;

export class MsalPlugin {
  constructor() {
    this.pluginOptions = {
      clientId: "",
      loginAuthority: "",
      passwordAuthority: "",
      knownAuthority: "",
      redirectUrl: "",
    };

    this.isAuthenticated = false;
    this.userGroups = [];
  }

  install(vue, options) {
    if (!options) {
      throw new Error("MsalPluginOptions must be specified");
    }
    this.pluginOptions = options;
    this.initialize(options);
    msalPluginInstance = this;
    vue.prototype.$msal = Vue.observable(msalPluginInstance);
  }

  initialize(options) {
    const msalConfig = {
      auth: {
        clientId: options.clientId,
        authority: options.loginAuthority,
        knownAuthorities: [options.knownAuthority],
        redirectUrl: options.redirectUrl
      },
      system: {
        loggerOptions: {
          loggerCallback: (level, message, containsPii) => {
            if (containsPii) {
              return;
            }
            switch (level) {
            case msal.LogLevel.Error:
              return;
            case msal.LogLevel.Info:
              return;
            case msal.LogLevel.Verbose:
              return;
            case msal.LogLevel.Warning:
              return;
            }
          },
          piiLoggingEnabled: false,
          logLevel: msal.LogLevel.Verbose
        }
      },
      cache: {
        cacheLocation: "sessionStorage",
      }

    };
    msalInstance = new msal.PublicClientApplication(msalConfig);
    this.isAuthenticated = this.getIsAuthenticated();
  }

  async signIn() {

    try {
      sessionStorage.clear();
      const loginRequest = {
        scopes: ["openid", "profile", "offline_access"],
        redirectUri: this.pluginOptions.redirectUrl,
        prompt: "select_account"
      };

      const loginResponse = await msalInstance.loginPopup(loginRequest);

      if (loginResponse.idTokenClaims && loginResponse.idTokenClaims.groups) {
        this.userGroups = loginResponse.idTokenClaims.groups;
      }
      if (this._hasAccess(this.userGroups)) {
        this.isAuthenticated = Boolean(loginResponse.account);
        const accessToken = await this.acquireToken();
        return accessToken;
      }
      else {
        throw new Error("ACCESS_DENIED", { cause: "INSUFFICIENT_PERMISSIONS" });
      }

    } catch (err) {
      if (err.errorMessage && err.errorMessage.indexOf("AADB2C90118") > -1) {
        try {
          const passwordResetResponse = await msalInstance.loginPopup({
            scopes: ["openid", "profile", "offline_access"],
            authority: this.pluginOptions.passwordAuthority
          });
          this.isAuthenticated = Boolean(passwordResetResponse.account);
          return;
        } catch (resetErr) {
          throw new Error("PASSWORD_RESET_FAILED", { cause: resetErr.errorMessage || "Password reset failed" });
        }
      }

      if (err.message === "ACCESS_DENIED") {
        throw new Error("ACCESS_DENIED", { cause: "Your user account does not have the required permissions to access this application" });
      }

      if (err.errorCode === "user_cancelled" || err.errorMessage.indexOf("user_cancelled") > -1) {
        return;
      }

      throw new Error("LOGIN_FAILED", { cause: err.errorMessage || "An unexpected error occurred during login" });
    }
  }

  _hasAccess(userGroups) {
    const groupId = process.env.VUE_APP_GROUP_ID;
    return userGroups.includes(groupId);
  }

  async signOut() {
    try {
      await msalInstance.logoutRedirect({
        postLogoutRedirectUri: this.pluginOptions.redirectUrl,
      });
      this.isAuthenticated = false;
    } catch (error) {
      this.isAuthenticated = false;
    }
  }

  async acquireToken() {
    const request = {
      account: msalInstance.getAllAccounts()[0],
      scopes: [
        `api://${process.env.VUE_APP_AZURE_AD_CLIENT_ID}/.default`,
      ]
    };
    try {
      const response = await msalInstance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (error) {
      if (error instanceof msal.InteractionRequiredAuthError) {
        return msalInstance.acquireTokenPopup(request).catch(() => {
          this.isAuthenticated = false;
        });
      }
      return null;
    }
  }

  getIsAuthenticated() {
    const accounts = msalInstance.getAllAccounts();
    if (accounts && accounts.length > 0) {
      if (this.userGroups.length === 0) {
        const currentAccount = accounts[0];
        if (currentAccount.idTokenClaims && currentAccount.idTokenClaims.groups) {
          this.userGroups = currentAccount.idTokenClaims.groups;
        }
      }
      return this._hasAccess(this.userGroups);
    }
    return false;
  }
}
